import { FC, ChangeEvent, ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { useQuery } from '@apollo/client';

import {
  AppBar,
  FormControl as _FormControl,
  LinearProgress,
  MenuItem,
  Select as _Select,
  Tabs as _Tabs,
  Tab,
} from '@material-ui/core';

import {
  GetProjectDocument,
  GetProjectQuery,
  GetProjectQueryVariables,
  GetVariationDocument,
  GetVariationQuery,
  GetVariationQueryVariables,
} from '../../generated/graphql';
import useQuerySearchParams from '../../hooks/useQuerySearchParams';
import useEditor from '../../hooks/useEditor';

import { PageTitle } from '../component/primitive/TypographyUI';
import { Overview } from '../component/compound/VariationDetails';
import { VersionHistory } from '../component/compound/VariationDetails';
import { Comments } from '../component/compound/VariationDetails';

type Params = {
  experiment_id: string;
  variation_id: string;
};

type Props = {
  children: ReactNode | ReactNode[];
};

const VariationDetailsPage: FC<Props> = () => {
  const { getParam, setParam } = useQuerySearchParams();
  const { experiment_id, variation_id } = useParams<Params>();
  const [tabId, setTabId] = useState(getParam('tab') ?? 'overview');

  const {
    data: { project } = { project: null },
    loading: projectLoading,
    refetch: projectRefetch,
  } = useQuery<GetProjectQuery, GetProjectQueryVariables>(GetProjectDocument, {
    variables: { id: experiment_id! },
    context: { clientName: 'planbcd' },
  });
  const {
    data: { variation } = { variation: null },
    loading: variationLoading,
    refetch: variationRefetch,
  } = useQuery<GetVariationQuery, GetVariationQueryVariables>(GetVariationDocument, {
    variables: { id: variation_id! },
    context: { clientName: 'planbcd' },
  });

  const refetch = () => {
    projectRefetch();
    variationRefetch();
  };

  const openEditor = useEditor({ variation, project });

  const onSelectChange = (event: ChangeEvent<{ value: unknown }>) => {
    if (event.target.value === 'check') openEditor({ editable: false });
    openEditor({ cloning: true });
  };

  const hasCopyAndEditorPermit = useMemo(() => {
    if (variation?.permissions?.copy?.permit && variation?.permissions.show_in_editor?.permit) {
      return true;
    }
    return false;
  }, [variation]);

  const onTabChange = (_: any, value: string) => {
    setParam('tab', value);
    setTabId(value);
  };

  const SelectedTabContent = useMemo(() => {
    if (!variation || !project) return null;
    switch (tabId) {
      default:
      case 'overview':
        return (
          <Overview
            project={project}
            rounds={project.rounds}
            variation={variation}
            onTabChange={onTabChange}
            refetch={refetch}
          />
        );
      case 'versions':
        return <VersionHistory project={project} variation={variation} />;
      case 'comments':
        return <Comments variation={variation} variationRefetch={variationRefetch} />;
    }
  }, [project, variation, tabId]);

  if (variationLoading || projectLoading) return <LinearProgress />;
  if (!variation || !project) return <div>no data</div>;

  return (
    <Root>
      <TitleFlex>
        <PageTitle variant="h2">{variation.plan_a ? 'オリジナルデザイン' : `デザイン案: #${variation.id}`}</PageTitle>
        <FormControl variant="standard">
          <Select disabled={!hasCopyAndEditorPermit} onChange={onSelectChange} value="title">
            <MenuItem value={'title'}>
              <em>デザイン案の操作</em>
            </MenuItem>
            <MenuItem value={'copy'}>このデザイン案を複製する</MenuItem>
            <MenuItem value={'check'}>UIエディターでチェックする</MenuItem>
          </Select>
        </FormControl>
      </TitleFlex>

      {/* タブ */}
      <AppBar color="transparent" position="static" style={{ margin: '15px 10px 45px 10px', boxShadow: 'none' }}>
        <Tabs value={tabId} onChange={onTabChange} indicatorColor="primary" textColor="primary" variant="scrollable">
          <Tab value="overview" label={'概要'} disableRipple />
          <Tab value="versions" label={'編集履歴'} disableRipple />
          <Tab
            value="comments"
            label={`コメント${variation.comment_counts?.total ? `（${variation.comment_counts.total}）` : ''}`}
            disableRipple
          />
        </Tabs>
      </AppBar>
      {SelectedTabContent}
    </Root>
  );
};

const Root = styled('div')({
  padding: '0 24px',
});

const TitleFlex = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const FormControl = styled(_FormControl)(`
    border: 1.5px solid #E6E7E8;
    border-radius: 4px;
    height: 40px;
    width: 179px;
    .MuiInput-underline:after,.MuiInput-underline:before,.MuiInput-underline:hover:not(.Mui-disabled):before {
      border: none;
    }
`);

const Select = styled(_Select)(`
  background-color: white;
  height: 40px;
  font-weight: 700;
  color: #4C566C;
  outline: none;
  padding-left: 12px;
  &:hover:not(.Mui-disabled) {
    color: #707889;
    .MuiOutlinedInput-notchedOutline {
      border-color: #E6E7E8;
    }
  }
  .MuiSelect-root {
    background-color: transparent;
  }
  fieldset {
    border: 1.5px solid #E6E7E8;
    &:focus-visible {
      border: none;
    }
    &:focus {
      outline: none;
    }
  }
`);

const Tabs = styled(_Tabs)({
  '& .MuiTabs-scroller': {
    '& button.Mui-selected,button.MuiTab-root': {
      minWidth: 'unset',
      paddingLeft: '0',
      paddingRight: '0',
      marginRight: '30px',
      fontWeight: '700',
      fontFamily: 'Hiragino Sans',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: '#4C566C',
      fontWeight: '700',
    },
    '& .MuiTab-textColorPrimary': {
      color: '#707889', //commonization at src/view/constant/color.ts
      fontWeight: '700',
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#4C566C',
      height: '3px',
    },
  },
});

export default VariationDetailsPage;
